import { colorsPalettes } from "@similarweb/styles";
import type {
    RankingDistributionCompareModeData } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/rankingDistribution.types";
import {
    RankingDistributionMetric,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/rankingDistribution.types";
import { abbrNumberFilter, trimZeroPrefixFromNumberedString } from "app/filters/ngFilters";
import { numbersFilter } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";
import ReactDOMServer from "react-dom/server";
import type {
    IPoint } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionChartTooltip";
import {
    ChannelsOverviewChartTooltip,
    TableSubtitle,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionChartTooltip";
import { translate } from "app/filters/i18nFilter";
import { RANKING_DISTRIBUTION_DICTIONARY } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionTranslator";
import { MiniFlexTable } from "@similarweb/ui-components";
import { percentsFilter } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/Utilities";
import { ChangeCellWrapper, MiniFlexTableWrapper, TooltipTitle } from "./StyledComponents";
import { TOOLTIP_COLUMN_FIELD, TOOLTIP_METRIC } from "./rankingDistributionConfigs";
import { ChangePercentage, DefaultCellRightAlign } from "app/components/React/Table/cells";
import { DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { CellWithFontVariant } from "app/components/React/Table/cells/CellWithFontVariant";
import { NO_DATA_SIGN } from "app/filters/const";

const getByWebsiteCategories = (data: RankingDistributionCompareModeData) => {
    return data.map((dataUnit) => {
        return dataUnit.data.map(({ key }) => key);
    })[0];
};

const getByPositionsCategories = (data: RankingDistributionCompareModeData) => {
    return data.length
        ? data[0].data.map(({ key }) =>
            translate(RANKING_DISTRIBUTION_DICTIONARY.BY_POSITION_CATEGORIES, {
                  position: `${trimZeroPrefixFromNumberedString(key, NO_DATA_SIGN)}`,
              }),
          )
        : null;
};

type TooltipPointLite = { point?: { key?: string } };

function getTooltipTitle(points: TooltipPointLite[], metric: RankingDistributionMetric): string {
    const firstPoint = points?.[0];
    const rawKey = firstPoint?.point?.key ?? "";

    if (metric === RankingDistributionMetric.ByPosition) {
        const position = trimZeroPrefixFromNumberedString(String(rawKey), "-");
        return translate(RANKING_DISTRIBUTION_DICTIONARY.BY_POSITION_TOOLTIP, { position });
    }

    const website = String(rawKey);
    return translate(RANKING_DISTRIBUTION_DICTIONARY.BY_WEBSITE_TOOLTIP, { website });
}

const RankingDistributionChartTooltipContent = ({
    pointsList,
    metric,
    tooltipSubtitle,
    translatedWords,
}: {
    pointsList: IPoint[];
    metric: RankingDistributionMetric;
    tooltipSubtitle: string;
    translatedWords: Record<string, string>;
}) => {
    return (
        <>
            <TooltipTitle>{translatedWords.title}</TooltipTitle>
            <TableSubtitle>{tooltipSubtitle}</TableSubtitle>
            <MiniFlexTableWrapper>
                <MiniFlexTable
                    data={getFlexTableData(pointsList, metric)}
                    columns={getFlexTableColumns(metric, translatedWords)}
                    translations={{
                        outOf: translate("common.minitable.pager.out_of"),
                        pages: translate("Common.minitable.pager.pages"),
                    }}
                />
            </MiniFlexTableWrapper>
        </>
    );
};

const RankingDistributionTableDefaultWrapper = ({ children }) => {
    return <CellWithFontVariant variant={"body2"}>{children}</CellWithFontVariant>;
};

const getFlexTableColumns = (metric: RankingDistributionMetric, translatedWords: Record<string, string>) => [
    TOOLTIP_METRIC[metric](translatedWords),
    {
        field: "keywordsCount",
        displayName: translatedWords.keywords,
        width: 76,
        cellComponent: (props) => {
            return (
                <RankingDistributionTableDefaultWrapper>
                    <DefaultCellRightAlign {...props} />
                </RankingDistributionTableDefaultWrapper>
            );
        },
        cellClass: "keywords-count",
        headerComponent: DefaultCellHeaderRightAlign,
    },
    {
        field: "percentage",
        displayName: translatedWords.ofSiteKeywords,
        width: 79,
        cellComponent: (props) => {
            return (
                <RankingDistributionTableDefaultWrapper>
                    <DefaultCellRightAlign {...props} />
                </RankingDistributionTableDefaultWrapper>
            );
        },
        headerComponent: DefaultCellHeaderRightAlign,
        cellClass: "percentage",
    },
    {
        field: "change",
        displayName: translatedWords.change,
        width: 86,
        headerComponent: DefaultCellHeaderRightAlign,
        cellComponent: (props) => {
            return (
                <ChangeCellWrapper>
                    <RankingDistributionTableDefaultWrapper>
                        <ChangePercentage {...props} />
                    </RankingDistributionTableDefaultWrapper>
                </ChangeCellWrapper>
            );
        },
    },
];

const getFlexTableData = (pointsList: IPoint[], metric: RankingDistributionMetric) => {
    return pointsList.map(({ color, name, numberOfKeywords, percentageFromTotal, change }) => {
        return {
            [TOOLTIP_COLUMN_FIELD[metric]]: {
                color,
                name,
            },
            keywordsCount: abbrNumberFilter()(numberOfKeywords),
            percentage: percentsFilter(percentageFromTotal),
            change: change,
        };
    });
};

export const getConfig = ({
    metric,
    data,
    translatedWords,
    tooltipSubtitle,
}: {
    metric: RankingDistributionMetric;
    data: RankingDistributionCompareModeData;
    translatedWords: Record<string, string>;
    tooltipSubtitle: string;
}) => {
    return {
        chart: {
            height: 220,
            spacingTop: 20,
            type: "column",
        },
        colors: data.map(({ color }) => color),
        xAxis: {
            categories:
                metric === RankingDistributionMetric.ByPosition
                    ? getByPositionsCategories(data)
                    : getByWebsiteCategories(data),
            lineColor: colorsPalettes.carbon[100],
            gridLineColor: colorsPalettes.carbon[50],
            tickWidth: 0,
            align: "center",
            rotation: 0,
            useHTML: true,
            style: {
                fontSize: "14px",
                textAlign: "center",
                fontFamily: '"Roboto", sans-serif',
            },
        },
        yAxis: {
            gridLineColor: colorsPalettes.navigation["ICON_BAR_BACKGROUND"],
            min: null,
            max: null,
            startOnTick: false,
            endOnTick: true,
            title: null,
            opposite: false,
            showLastLabel: true,
            color: colorsPalettes.carbon[400],
            labels: {
                align: "right",
                enabled: true,
                useHTML: true,
                x: -10,
                formatter() {
                    return abbrNumberFilter()(this.value);
                },
                style: {
                    fontSize: "12px",
                    textTransform: "uppercase",
                    fontFamily: '"Roboto", sans-serif',
                    color: colorsPalettes.carbon[400],
                },
            },
        },
        legend: {
            enabled: false,
        },
        plotOptions: {
            column: {
                borderWidth: 0,
                pointWidth: null,
                minPointLength: 3,
                grouping: true,
                maxPointWidth: 40,
                pointPadding: 0.15,
                pointRange: 1,
                dataLabels: {
                    enabled: false,
                    crop: false,
                    formatter: function () {
                        return this.point.y ? numbersFilter(this.point.y) : "N/A";
                    },
                    color: colorsPalettes.carbon[300],
                    useHTML: true,
                    style: {
                        fontFamily: '"Roboto", sans-serif',
                        fontSize: "14px",
                        fontWeight: "400",
                    },
                    overflow: "none",
                },
                states: {
                    hover: {
                        enabled: false,
                    },
                },
            },
        },
        tooltip: {
            enabled: true,
            shared: true,
            outside: true,
            followPointer: false,
            useHTML: true,
            backgroundColor: undefined,
            borderWidth: 0,
            formatter: function () {
                const pointsList: IPoint[] = this.points.reduce((acc, point) => {
                    return [
                        ...acc,
                        {
                            numberOfKeywords: point.point.y,
                            color: point.point.color,
                            name: point.series.name,
                            percentageFromTotal: point.point.percentageFromTotal,
                            change: point.point.change,
                        },
                    ];
                }, []);
                return ReactDOMServer.renderToString(
                    <ChannelsOverviewChartTooltip width={"389px"}>
                        <RankingDistributionChartTooltipContent
                            tooltipSubtitle={tooltipSubtitle}
                            pointsList={pointsList}
                            metric={metric}
                            translatedWords={{
                                ...translatedWords,
                                title: getTooltipTitle(this.points, metric),
                            }}
                        />
                    </ChannelsOverviewChartTooltip>,
                );
            },
        },
    };
};
